// Tunable shapes for the smooth/lumpy takeoff sketches, expressed as
// plain-English knobs instead of raw SVG path coordinates. To fine-tune,
// edit the numbers in SMOOTH_CURVE / LUMPY_CURVE below — or use the live
// sliders on /figures-code and paste the emitted consts back into this file.
//
// "Capability" is in × human, mapped through the scale in simpleTakeoffLayout
// (0.01× at the bottom of the plot, 100× at the top; Human Level = 1×).
//
// Both curves ramp in LOG-capability space, so on the log-scale plot they are
// slightly accelerating from the very first pixel (superexponential growth,
// the shape of the Plan D uplift curve in plan-capabilities/upliftSeries.ts)
// rather than hugging a flat floor. The lumpy curve follows the smooth
// trajectory exactly until its kink, then bends up harder — so the two
// sketches are identical for the pre-kink stretch by construction.

import { PLOT_L, PLOT_R, capabilityY, capabilityAtY } from './simpleTakeoffLayout';

export type SmoothCurveParams = {
  capabilityAtLeftEdge: number;
  capabilityAtRightEdge: number;
  accelerationSharpness: number;
};

export const SMOOTH_CURVE: SmoothCurveParams = {
  // Capability (× human) at the left edge — below 1 starts the curve visibly
  // under the Human Level line.
  capabilityAtLeftEdge: 0.1,
  // Capability (× human) the curve has reached when it exits the right edge.
  capabilityAtRightEdge: 150,
  // Higher = gentler early rise, harder late climb (the early slope never
  // hits zero — the curve accelerates the whole way, Plan-D style).
  accelerationSharpness: 4,
};

export type LumpyCurveParams = {
  kinkPositionAcross: number;
  climbWidthAcross: number;
  postKinkSharpness: number;
};

export const LUMPY_CURVE: LumpyCurveParams = {
  // How far across the plot the kink happens (0 = left edge, 1 = right edge).
  // Everything before it IS the smooth curve — same knobs, same pixels.
  kinkPositionAcross: 0.65,
  // Width of the post-kink climb, as a fraction of the plot
  // (smaller = the jump looks more vertical).
  climbWidthAcross: 0.12,
  // Higher = the climb keeps accelerating and exits the top more vertically;
  // lower = closer to a straight steep line from the kink.
  postKinkSharpness: 1.5,
};

const PLOT_W = PLOT_R - PLOT_L;
const SAMPLES_PER_PIECE = 120;

// Fraction of a (log-)capability range covered at progress f (0 → 1): starts
// slow, ends fast. Sharpness k controls how back-loaded the growth is;
// k → 0 is linear.
function acceleratingRamp(f: number, k: number): number {
  if (Math.abs(k) < 1e-9) return f;
  return (Math.exp(k * f) - 1) / (Math.exp(k) - 1);
}

function toPath(points: Array<readonly [number, number]>): string {
  return points
    .map(([x, y], i) => `${i === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`)
    .join(' ');
}

// Capability (× human) of the smooth curve at fraction f across the plot:
// an accelerating ramp in log space, so growth is superexponential.
function smoothCapabilityAt(f: number, p: SmoothCurveParams): number {
  const logStart = Math.log(p.capabilityAtLeftEdge);
  const logEnd = Math.log(p.capabilityAtRightEdge);
  const frac = acceleratingRamp(f, p.accelerationSharpness);
  return Math.exp(logStart + (logEnd - logStart) * frac);
}

// The smooth trajectory sampled from plot-fraction fStart to fEnd — used for
// the whole smooth curve, and for the lumpy curve's pre-kink stretch.
function smoothPoints(
  fStart: number,
  fEnd: number,
  p: SmoothCurveParams,
): Array<readonly [number, number]> {
  const pts: Array<readonly [number, number]> = [];
  for (let i = 0; i <= SAMPLES_PER_PIECE; i++) {
    const f = fStart + (i / SAMPLES_PER_PIECE) * (fEnd - fStart);
    pts.push([PLOT_L + f * PLOT_W, capabilityY(smoothCapabilityAt(f, p))] as const);
  }
  return pts;
}

// A log-space accelerating ramp between two capabilities over [xStart, xEnd].
function logRampPoints(
  xStart: number,
  xEnd: number,
  capStart: number,
  capEnd: number,
  sharpness: number,
): Array<readonly [number, number]> {
  const pts: Array<readonly [number, number]> = [];
  const logStart = Math.log(capStart);
  const logEnd = Math.log(capEnd);
  for (let i = 0; i <= SAMPLES_PER_PIECE; i++) {
    const f = i / SAMPLES_PER_PIECE;
    const cap = Math.exp(logStart + (logEnd - logStart) * acceleratingRamp(f, sharpness));
    pts.push([xStart + f * (xEnd - xStart), capabilityY(cap)] as const);
  }
  return pts;
}

export function smoothTakeoffPath(p: SmoothCurveParams): string {
  return toPath(smoothPoints(0, 1, p));
}

export function lumpyTakeoffPath(
  p: LumpyCurveParams,
  smooth: SmoothCurveParams = SMOOTH_CURVE,
): string {
  const kinkX = PLOT_L + p.kinkPositionAcross * PLOT_W;
  const exitX = kinkX + p.climbWidthAcross * PLOT_W;
  // Up to the kink, the curve IS the smooth takeoff — identical pixels.
  const creep = smoothPoints(0, p.kinkPositionAcross, smooth);
  // The jump climbs from wherever the smooth trajectory sits at the kink to
  // the very top of the viewBox (y = 0); the sudden slope change at the join
  // is what makes the visible corner.
  const jump = logRampPoints(
    kinkX, exitX,
    smoothCapabilityAt(p.kinkPositionAcross, smooth), capabilityAtY(0),
    p.postKinkSharpness,
  );
  return toPath([...creep, ...jump.slice(1)]);
}
